import { useState } from 'react'
import { Chip, Skeleton, Tooltip } from '@mui/material'
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined'
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined'
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined'
import { com } from '@eidu/web'
import { PublishStatus } from './usePublishStatus'
import ResourceDiffDialog from './ResourceDiffDialog'
import EntityType = com.eidu.sharedlib.entity.type.EntityType

const shortVersionId = (versionId: string): string => versionId.split('-')[0] ?? versionId.slice(0, 8)

const chipSx = {
  pl: 0.75,
  '& .MuiChip-icon': { ml: 0, mr: 0.5 },
} as const

type PublishStatusChipProps = {
  status: PublishStatus
  entityType?: EntityType
  currentResourceKey: string | undefined
}

const PublishStatusChip = ({ status, entityType, currentResourceKey }: PublishStatusChipProps) => {
  const [diffOpen, setDiffOpen] = useState(false)

  if (status.state === 'loading') return <Skeleton variant="rounded" width={96} height={24} />
  if (status.state === 'error') return null

  if (status.state === 'published')
    return (
      <Tooltip title={`Published version ${status.latest.versionId}`}>
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<CloudDoneOutlinedIcon />}
          label={`Published · ${shortVersionId(status.latest.versionId)}`}
          sx={chipSx}
        />
      </Tooltip>
    )

  if (status.reason === 'never-published')
    return (
      <Tooltip title="This asset has never been published.">
        <Chip
          size="small"
          color="default"
          variant="outlined"
          icon={<CloudOffOutlinedIcon />}
          label="Unpublished"
          sx={{
            ...chipSx,
            color: 'text.disabled',
            borderColor: 'text.disabled',
            '& .MuiChip-icon': {
              ...chipSx['& .MuiChip-icon'],
              color: 'text.disabled',
              fontSize: 16,
            },
          }}
        />
      </Tooltip>
    )

  const publishedPayloadKey = status.latest.payloadKey ?? undefined
  const canDiff =
    !!status.diff &&
    (status.diff.fieldChanges.length > 0 ||
      status.diff.linkChanges.length > 0 ||
      (!!publishedPayloadKey && !!currentResourceKey))

  return (
    <>
      <Tooltip
        title={canDiff ? 'View unpublished changes' : 'This asset has unpublished changes since the last version.'}
      >
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          icon={<EditNoteOutlinedIcon />}
          label="Draft"
          onClick={canDiff ? () => setDiffOpen(true) : undefined}
          clickable={canDiff}
          sx={chipSx}
        />
      </Tooltip>
      {diffOpen && canDiff && (
        <ResourceDiffDialog
          onClose={() => setDiffOpen(false)}
          leftKey={publishedPayloadKey}
          rightKey={currentResourceKey}
          leftLabel={`Published · ${shortVersionId(status.latest.versionId)}`}
          rightLabel="Current draft"
          title="Unpublished changes"
          entityDiff={status.diff}
          entityType={entityType}
        />
      )}
    </>
  )
}

export default PublishStatusChip
